import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getShows } from "@/api/shows.api";
import { useAuth } from "./AuthContext";
import { apiPut } from "@/api/client";
import type { ShowListItem } from "@shared/types/shows";

interface ShowState {
  shows: ShowListItem[];
  selectedShowId: number | null;
  selectedShow: ShowListItem | null;
  selectShow: (id: number) => void;
  selectNewestShow: () => void;
  isLoading: boolean;
}

const ShowContext = createContext<ShowState | null>(null);

const STORAGE_KEY = "smt_selected_show";

export function ShowProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const serverConsumed = useRef(false);

  const [selectedShowId, setSelectedShowId] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });

  const { data: shows = [], isLoading } = useQuery({
    queryKey: ["shows"],
    queryFn: () => getShows(),
    staleTime: 60_000,
  });

  // Server-side show override: consume once on load, then clear
  useEffect(() => {
    if (user?.selectedShowId && !serverConsumed.current && shows.length > 0) {
      serverConsumed.current = true;
      if (shows.some(s => s.id === user.selectedShowId)) {
        setSelectedShowId(user.selectedShowId);
        localStorage.setItem(STORAGE_KEY, String(user.selectedShowId));
        // Clear server-side flag so it doesn't override future selections
        apiPut("/api/profile/clear-selected-show", {}).catch(() => {});
      }
    }
  }, [user?.selectedShowId, shows]);

  const selectShow = useCallback((id: number) => {
    setSelectedShowId(id);
    localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  const selectNewestShow = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    const psShows = shows.filter(s => s.first_perf_date && s.first_perf_date > today);
    const target = psShows.length > 0 ? psShows[psShows.length - 1] : shows[shows.length - 1];
    if (target) {
      setSelectedShowId(target.id);
      localStorage.setItem(STORAGE_KEY, String(target.id));
    }
  }, [shows]);

  // Use stored show if it exists in the user's show list, otherwise first show
  const storedValid = selectedShowId && shows.some(s => s.id === selectedShowId);
  const effectiveId = storedValid ? selectedShowId : shows[0]?.id ?? null;
  const selectedShow = shows.find(s => s.id === effectiveId) ?? null;

  // Clear stale localStorage when stored show is no longer in user's list.
  // No setState needed: render already falls back via effectiveId above, so
  // this only persists the fallback (avoids a cascading re-render).
  useEffect(() => {
    if (shows.length > 0 && selectedShowId && !shows.some(s => s.id === selectedShowId)) {
      const fallback = shows[0]?.id ?? null;
      if (fallback) {
        localStorage.setItem(STORAGE_KEY, String(fallback));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [shows, selectedShowId]);

  return (
    <ShowContext.Provider value={{
      shows,
      selectedShowId: effectiveId,
      selectedShow,
      selectShow,
      selectNewestShow,
      isLoading,
    }}>
      {children}
    </ShowContext.Provider>
  );
}

export function useShows(): ShowState {
  const ctx = useContext(ShowContext);
  if (!ctx) throw new Error("useShows must be used within ShowProvider");
  return ctx;
}
